import * as Sentry from "@sentry/angular";
import {ElementRef, ErrorHandler, inject, LOCALE_ID, NgModule, provideAppInitializer} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { LocationStrategy, PathLocationStrategy, registerLocaleData } from '@angular/common';
import localeDECH from "@angular/common/locales/de-CH";

import { AppComponent } from './app.component';
import { NavigationComponent } from './shared/navigation/navigation.component';
import { PageForbiddenComponent } from './authentication/page-forbidden/page-forbidden.component';
import {AppRoutingModule} from "./app-routing.module";
import { InputsModule } from '@progress/kendo-angular-inputs';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {Router, RouterModule} from "@angular/router";
import { LayoutModule } from '@progress/kendo-angular-layout';
import {ReactiveFormsModule} from "@angular/forms";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import {AuthenticationInterceptor} from "./shared/interceptors/authentication.interceptor";
import {SharedModule} from "./shared/shared.module";
import {ResourceSchedulerModule} from "./scheduler/resource-scheduler.module";
import {SettingModule} from "./setting/setting.module";
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';

// Load all required data for the ES locale
import "@progress/kendo-angular-intl/locales/de-CH/all";
import { ErrorCatchingInterceptor } from './shared/interceptors/error-catching.interceptor';
import {HomeModule} from "./home/home.module";
import { TenantModule } from './tenant/tenant.module';
import { EmployeeModule } from './employee/employee.module';
import { AuthenticationModule } from './authentication/authentication.module';
import { TimeTrackingModule } from './time-tracking/time-tracking.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { ContactModule } from './contact/contact.module';
import { TranslocoRootModule } from './transloco-root.module';
import { AssignmentModule } from './assignment/assignment.module';
import { ExpensesModule } from './expenses/expenses.module';
import { NOTIFICATION_CONTAINER, NotificationService } from '@progress/kendo-angular-notification';
import { LoadingIndicationInterceptor } from './shared/interceptors/loading-indication.interceptor';
import { VersionCheckInterceptor } from './shared/interceptors/version-check.interceptor';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { FiscalYearsEffects, fiscalYearsReducers } from './stores/global/fiscal-years/fiscal-years.store';
import { CurrentEmployeeEffects, currentEmployeeReducers } from './stores/global/current-employee/current-employee.store';
import { EmployeesEffects, employeesReducers } from './stores/global/employees/employees.store';
import { TimeSupplementsEffects, timeSupplementsReducer } from "./stores/global/time-supplements/time-supplements.store";



registerLocaleData(localeDECH);

@NgModule({ declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent], 
    imports: [
        SharedModule,
        BrowserModule,
        AppRoutingModule,
        InputsModule,
        BrowserAnimationsModule,
        RouterModule,
        LayoutModule,
        ReactiveFormsModule,
        SharedModule,
        TranslocoRootModule,
        ResourceSchedulerModule,
        SettingModule,
        DropDownsModule,
        HomeModule,
        TenantModule,
        EmployeeModule,
        AuthenticationModule,
        TimeTrackingModule,
        ContactModule,
        AssignmentModule,
        ExpensesModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000'
        }),
        StoreModule.forRoot({
            fiscalYears: fiscalYearsReducers,
            currentEmployee: currentEmployeeReducers,
            employees: employeesReducers,
            timeSupplements: timeSupplementsReducer
        }, {}),
        EffectsModule.forRoot([
            FiscalYearsEffects,
            CurrentEmployeeEffects,
            EmployeesEffects,
            TimeSupplementsEffects
        ])], 
        providers: [
        {
            provide: NOTIFICATION_CONTAINER,
            useFactory: () => {
                //return the container ElementRef, where the notification will be injected
                return { nativeElement: document.body } as ElementRef;
            }
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthenticationInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorCatchingInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoadingIndicationInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: VersionCheckInterceptor,
            multi: true
        },
        { provide: LOCALE_ID, useValue: "de-CH" },
        { provide: NotificationService, useClass: NotificationService },
        Location,
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        provideHttpClient(withInterceptorsFromDi()),

        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
              showDialog: false,
            }),
          }, {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          provideAppInitializer(() => {
            inject(Sentry.TraceService);
          }),
    ] })
export class AppModule { }
